import { computed, inject, Injectable, signal } from '@angular/core';
import { httpResource } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

import { CatalogOperationsFacade } from '../../../Facades/catalog-operations.facade';
import { ApiService } from '../../Shared/Services/api.service';
import { NavigationLockService } from '../../Shared/Services/navigation-lock.service';
import { ApplicationDemandsService } from '../Services/application-demands.service';
import { DemandCreatedToastService } from '../Services/demand-created-toast.service';
import { endpoints } from '../../../../environments/endpoints';
import {
  ApplicationDetail,
  ApplicationDetailResponse,
} from '../../../Interfaces/api/applications/detail.interface';
import {
  ManagedDevice,
  ManagedDevicesResponse,
} from '../../../Interfaces/api/applications/managed-devices.interface';
import { ApplicationDemand } from '../../../Models/avyna/application-demand.model';
import { AssignmentTarget } from '../../../Enums/assignment-target.enum';
import { UserDemandEntry } from '../Interfaces/user-demands-list.interface';

@Injectable()
export class ApplicationDetailFacade {
  private readonly _api = inject(ApiService);
  private readonly _demandsService = inject(ApplicationDemandsService);
  private readonly _catalogOperations = inject(CatalogOperationsFacade);
  private readonly _createdToast = inject(DemandCreatedToastService);
  private readonly _navLock = inject(NavigationLockService);

  private readonly _applicationId = signal<string>('');
  readonly applicationId = computed(() => this._applicationId());

  private readonly _submitting = signal<boolean>(false);
  readonly submitting = computed<boolean>(() => this._submitting());

  readonly application = httpResource<ApplicationDetailResponse>(() => {
    const id = this._applicationId();
    if (!id) return undefined;
    return this._api.get<ApplicationDetailResponse>(endpoints.api.catalog.applications.show(id), {
      skipLoading: true,
    });
  });

  readonly applicationData = computed<ApplicationDetail | undefined>(() =>
    this.application.hasValue() ? this.application.value().data : undefined,
  );

  readonly userDemand = computed<ApplicationDemand | undefined>(() => {
    const raw = this.applicationData()?.current_user_demand;
    return raw ? new ApplicationDemand(raw) : undefined;
  });

  readonly devices = httpResource<ManagedDevicesResponse>(() => {
    const id = this._applicationId();
    const app = this.applicationData();
    if (!id || !app || app.assignment_target !== AssignmentTarget.Device) return undefined;
    return this._demandsService.myDevices(id);
  });

  readonly deviceList = computed<ManagedDevice[]>(() =>
    this.devices.hasValue() ? this.devices.value().data : [],
  );

  readonly requiresApproval = computed<boolean>(() => {
    const app = this.applicationData();
    return !!app && (app.application_approvals?.length ?? 0) > 0;
  });

  readonly loading = computed<boolean>(
    () => this.application.isLoading() || this.devices.isLoading(),
  );

  readonly bottomEntries = computed<UserDemandEntry[]>(() => {
    const app = this.applicationData();
    if (!app) return [];

    if (app.assignment_target === AssignmentTarget.Device) {
      return this.deviceList()
        .filter((device) => device.latest_demand !== null)
        .map((device) => this.toDeviceEntry(device));
    }

    const demand = this.userDemand();
    return demand && demand.id ? [this.toUserEntry(demand)] : [];
  });

  setApplicationId(id: string): void {
    this._applicationId.set(id);
  }

  reset(): void {
    this._applicationId.set('');
  }

  async requestForUser(): Promise<void> {
    const id = this._applicationId();
    if (!id || this._submitting()) return;
    this._submitting.set(true);
    this._navLock.lock();
    try {
      const response = await firstValueFrom(this._demandsService.store(id, {}));
      this.application.reload();
      this._catalogOperations.reload();
      this._createdToast.announce(response);
    } catch {
      // The error interceptor already shows the API message as a toast.
    } finally {
      this._navLock.unlock();
      this._submitting.set(false);
    }
  }

  async requestForDevice(intuneDeviceId: string): Promise<void> {
    const id = this._applicationId();
    if (!id) return;
    this._navLock.lock();
    try {
      const response = await firstValueFrom(
        this._demandsService.store(id, { device_intune_id: intuneDeviceId }),
      );
      this.devices.reload();
      this._catalogOperations.reload();
      this._createdToast.announce(response);
    } catch {
      // The error interceptor already shows the API message as a toast.
    } finally {
      this._navLock.unlock();
    }
  }

  private toUserEntry(demand: ApplicationDemand): UserDemandEntry {
    return {
      demandId: demand.id ?? '',
      reference: demand.reference,
      status: demand.status ?? '',
      createdAt: demand.created_at ?? '',
    };
  }

  private toDeviceEntry(device: ManagedDevice): UserDemandEntry {
    const latest = device.latest_demand;
    return {
      demandId: latest?.id ?? '',
      reference: latest?.reference,
      status: latest?.status ?? '',
      createdAt: latest?.created_at ?? '',
      deviceName: device.device_name,
      deviceModel: device.model,
      deviceLastSync: device.last_sync_at,
    };
  }
}
